import { Data } from "rete/types/core/data"
import { NevoLevel, NevoProperty } from "./nevo"
import { Extent } from "ol/extent"
import { IMDProp } from "../reify_layer/imd"
import { Socket } from "rete"

interface BaseLayer {
  name: string
  visible: boolean
  opacity: number
  /** Shown on the public map when hovering the layer’s info icon. */
  comment?: string
}

interface SeasonYearOption {
  id: number
  name: string
  year: string
  season: string
}

export interface ColourMapATI {
  ancient: [r: number, g: number, b: number, a: number]
  veteran: [r: number, g: number, b: number, a: number]
  notable: [r: number, g: number, b: number, a: number]
  public: [r: number, g: number, b: number, a: number]
  private: [r: number, g: number, b: number, a: number]
}

export interface StrokeFill {
  stroke: [r: number, g: number, b: number, a: number]
  fill: [r: number, g: number, b: number, a: number]
  strokeWidth?: number
}

export interface VectorField {
  name: string
  type?: string
}

export interface KewOption {
  value: string
  label: string
  selected?: boolean
  max?: number
  socket?: Socket
}

export interface KewCrownOption {
  name: string
  label: string
}

export type fillType = "greyscale" | "heatmap" | "jet" | "hsv" | "hot" | "cool" | "spring" | "summer" | "autumn" | "winter" | "copper" | "WIGnBu" | "greens" | "YIOrRd" | "bluered" | "RdBu" | "BuRd" | "picnic" | "rainbow" | "portland" | "blackbody" | "earth" | "electric" | "viridis" | "inferno" | "magma" | "plasma" | "warm" | "cool" | "rainbow-soft" | "bathymetry" | "cdom" | "chlorophyll" | "density" | "freesurface-blue" | "freesurface-red" | "oxygen" | "par" | "phase" | "salinity" | "temperature" | "turbidity" | "velocity-blue" | "velocity-green" | "cubehelix"

export interface OsmLayer extends BaseLayer {
  type: "OsmLayer"
}

export interface MapTileLayer extends BaseLayer {
  type: "MapTileLayer"
  id: number
}

export interface OverlayLayer extends BaseLayer {
  type: "OverlayLayer"
  id: number
  strokeWidth: number
  fillOpacity: number
}

export interface AtiLayer extends BaseLayer {
  type: "AtiLayer"
  colors: ColourMapATI
}

export interface ShapeLayer extends BaseLayer {
  type: "ShapeLayer"
  identifier: string
  colors: StrokeFill
  fill?: fillType
  minZoom?: number
  attribution?: string
  externalLink?: string
  vectorFields?: VectorField[]
  fieldValues?: Record<string, string[]>
  valueColors?: Record<string, Array<[number, number, number, number]>>
  selectedField?: string
}

export interface BoundaryLayer extends BaseLayer {
  type: "BoundaryLayer"
  identifier: string
  target: string
}

export interface KewLayer extends BaseLayer {
  type: "KewLayer"
  location: string
  periodOptions: KewOption[]
  typeOptions: KewOption[]
  metric: string
  loc?: string
}

export interface KewPointLayer extends BaseLayer {
  type: "KewPointLayer"
  identifier: string
  metric: number
  min?: number
  max?: number
  fill: fillType
  seasonYear: SeasonYearOption
}

export interface KewShapeLayer extends BaseLayer {
  type: "KewShapeLayer"
  identifier: string
  color: StrokeFill
  fill: fillType
  metric: KewCrownOption
  min?: number
  max?: number
}

export interface NevoLayer extends BaseLayer {
  type: "NevoLayer"
  level: NevoLevel
  property: NevoProperty
  fill: "greyscale" | "heatmap"
}

export interface ORValLayer extends BaseLayer {
  type: "ORValLayer",
  source: string
  style: string
}

export interface IMDLayer extends BaseLayer {
  type: "IMDLayer"
  year: number
  fill: fillType
  property: IMDProp
}

export interface CehLandCoverLayer extends BaseLayer {
  type: "CehLandCoverLayer"
  year: 2021 // TODO: allow the user to customise this
}

export interface CropMapLayer extends BaseLayer {
  type: "CropMapLayer"
  year: number
}

export interface ModelOutputLayer extends BaseLayer {
  type: "ModelOutputLayer"
  nodeId: number
  fill: fillType
  colors?: Array<[number, number, number, number]>
  /** When set, categorical colours follow label strings; legacy saves omit this and use `colors` index slots only. */
  colorByLabel?: Record<string, [number, number, number, number]>
  /** `"classes"` or `tableRow:{nodeId}:{rowIndex}` — maps categorical classes to table row values. */
  categoricalView?: string
  overrideBounds: boolean
  bounds: [min: number, max: number] | undefined
}

export interface DatasetLayer extends BaseLayer {
  type: "DatasetLayer"
  id: number
  fill: fillType
  colors?: Array<[number, number, number, number]>
  /** When set, categorical colours follow label strings; legacy saves omit this and use `colors` index slots only. */
  colorByLabel?: Record<string, [number, number, number, number]>
  /** `"classes"` or `tableRow:{nodeId}:{rowIndex}` — maps categorical classes to table row values. */
  categoricalView?: string
  overrideBounds: boolean
  bounds: [min: number, max: number] | undefined
  deleted?: boolean
}

export interface GeoserverLayer extends BaseLayer {
  type: "GeoserverLayer"
  layerName: string
  fill?: fillType
  xOffset?: number
  yOffset?: number
  attribution?: string
  externalLink?: string
  categoricalRaster?: boolean
  categoricalValueCount?: number
  rasterMinValue?: number
  rasterMaxValue?: number
  valueColors?: Array<[r: number, g: number, b: number, a: number]>
  uniqueValues?: number[]
  /** Present when a Boolean/Categorical (GeoTIFF) component is attached to the source upload. */
  categoryLabels?: Array<{ value: number; label: string; color?: string }>
}

/**
 * Small GeoServer coverage loaded once via WCS and rendered client-side as RGB
 * (ImageStatic). Used when WMS styling cannot show multiband reflectance correctly.
 */
export interface WcsRgbLayer extends BaseLayer {
  type: "WcsRgbLayer"
  /** GeoServer coverage id, e.g. `ind:layer_name`. */
  coverageId: string
  /** Native CRS bbox [minX, minY, maxX, maxY] used for the WCS subset. */
  nativeExtent: [number, number, number, number]
  /** CRS of `nativeExtent` (default EPSG:32651). */
  nativeCrs?: string
  /** 1-based band indices for R, G, B (default [1, 2, 3]). */
  rgbBands?: [number, number, number]
  /** Linear stretch floor before mapping to 0–255 (default 0). */
  stretchMin?: number
  /** Linear stretch ceiling; when omitted, uses a high percentile of nonzero samples. */
  stretchMax?: number
  /** WCS 2.0 scalefactor to keep the download small (default 0.35). */
  scaleFactor?: number
  /** GeoServer base URL including trailing slash; defaults to landscapes.wearepal.ai. */
  externalLink?: string
}

export interface WFSLayer extends BaseLayer {
  type: "WFSLayer"
  layer: string
  propIdx: number
  attribution: string
  fill: fillType
}

/** One row in the map sidebar legend for a Paintbrush vector layer. */
export interface PaintbrushLegendClass {
  /** Display label (matches GeoJSON feature `properties.className`). */
  className: string
  /** Hex colour from Paintbrush class definition. */
  color: string
}

/** Team Paintbrush annotation GeoJSON as a map vector layer (from layer palette). */
export interface PaintbrushGeoJSONLayer extends BaseLayer {
  type: "PaintbrushGeoJSONLayer"
  annotationId: number
  /** Same-origin path or full URL to GeoJSON (e.g. from annotations JSON `geojson_url`). */
  geojsonUrl: string
  /** Legend rows (from annotation `classes`, ordered). Omitted if none were returned by the API. */
  legendClasses?: PaintbrushLegendClass[]
  /** Class names hidden by the user in the map legend. */
  hiddenClassNames?: string[]
}

export type Layer = OsmLayer | MapTileLayer | OverlayLayer | NevoLayer | CehLandCoverLayer | ModelOutputLayer | DatasetLayer | CropMapLayer | AtiLayer | ShapeLayer | BoundaryLayer | GeoserverLayer | WcsRgbLayer | KewLayer | ORValLayer | IMDLayer | KewPointLayer | WFSLayer | KewShapeLayer | PaintbrushGeoJSONLayer

export interface Project {
  name: string
  extent?: Extent
  layer?: string
  cql?: string
  shape?: string
  externalLink?: string
  layers: Record<number, Layer>
  allLayers: number[]
  model: Data | null
}

export interface State {
  project: Project
  selectedLayer?: number
  hasUnsavedChanges: boolean
  autoProcessing: boolean
}

export const defaultProject: Project = {
  name: "Untitled project",
  layers: {
    1: { type: "OsmLayer", name: "OpenStreetMap", visible: true, opacity: 1 },
  },
  allLayers: [1],
  model: null
}
